import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const CoachSelectionStep = ({ coaches, selectedCoaches, onCoachToggle, onNext, onBack }) => {
  const [showAdvancedView, setShowAdvancedView] = useState(false);
  const [acknowledgedConflicts, setAcknowledgedConflicts] = useState(new Set());

  // Filter coaches based on view mode
  const getFilteredCoaches = () => {
    if (showAdvancedView) {
      return coaches; // Show all coaches including conflicted ones
    }
    return coaches.filter(
      (coach) =>
        coach.availability_status === 'fully_available' ||
        coach.availability_status === 'partly_available'
    );
  };

  const filteredCoaches = getFilteredCoaches();
  const conflictedCoaches = coaches.filter(
    (coach) =>
      coach.availability_status === 'coaching_other_crews' ||
      coach.availability_status === 'not_available' ||
      coach.availability_status === 'conflict'
  );

  const handleConflictAcknowledge = (coachId) => {
    const newAcknowledged = new Set(acknowledgedConflicts);
    if (newAcknowledged.has(coachId)) {
      newAcknowledged.delete(coachId);
    } else {
      newAcknowledged.add(coachId);
    }
    setAcknowledgedConflicts(newAcknowledged);
  };

  const getConflictValidation = () => {
    const selectedConflictedCoaches = selectedCoaches.filter((coachId) => {
      const coach = coaches.find((c) => c.id === coachId);
      return (
        coach &&
        (coach.availability_status === 'coaching_other_crews' ||
          coach.availability_status === 'not_available' ||
          coach.availability_status === 'conflict')
      );
    });

    const unacknowledgedConflicts = selectedConflictedCoaches.filter(
      (coachId) => !acknowledgedConflicts.has(coachId)
    );

    return {
      hasConflicts: selectedConflictedCoaches.length > 0,
      unacknowledgedConflicts: unacknowledgedConflicts.length,
      canProceed: unacknowledgedConflicts.length === 0,
    };
  };

  const validation = getConflictValidation();
  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Select Coaches</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAdvancedView(!showAdvancedView)}
              className="flex items-center gap-1 rounded bg-gray-100 px-3 py-1 text-sm text-gray-700 transition-colors hover:bg-gray-200"
            >
              <EyeIcon className="h-4 w-4" />
              {showAdvancedView ? 'Simple View' : 'Show All Coaches'}
            </button>
            {!showAdvancedView && conflictedCoaches.length > 0 && (
              <span className="rounded bg-amber-100 px-2 py-1 text-xs text-amber-700">
                {conflictedCoaches.length} coach(es) with conflicts hidden
              </span>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-600">
          Choose which coaches will supervise this training session.
          {showAdvancedView &&
            ' Advanced view shows all coaches including those with scheduling conflicts.'}
        </p>
      </div>

      {/* Conflict Warning */}
      {validation.hasConflicts && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <div className="flex-1">
              <h4 className="mb-1 text-sm font-medium text-amber-800">
                Scheduling Conflicts Detected
              </h4>
              <p className="mb-2 text-sm text-amber-700">
                {validation.unacknowledgedConflicts > 0
                  ? `${validation.unacknowledgedConflicts} selected coach(es) have conflicts that need acknowledgment.`
                  : 'All conflicts have been acknowledged. You may proceed with scheduling.'}
              </p>
              {validation.unacknowledgedConflicts > 0 && (
                <p className="text-xs text-amber-600">
                  Review the conflicts below and acknowledge them to continue.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {filteredCoaches.map((coach) => {
          const isSelected = selectedCoaches.includes(coach.id);
          const hasConflicts =
            coach.availability_status === 'coaching_other_crews' ||
            coach.availability_status === 'not_available' ||
            coach.availability_status === 'conflict';
          const isConflictAcknowledged = acknowledgedConflicts.has(coach.id);
          const coachDeclaredUnavailable =
            coach.availability_details?.declared_unavailable_for_session;

          const getAvailabilityIcon = () => {
            switch (coach.availability_status) {
              case 'fully_available':
                return '✅';
              case 'partly_available':
                return '⚠️';
              case 'coaching_other_crews':
                return '🔄';
              case 'not_available':
                return coachDeclaredUnavailable ? '⊘' : '❌';
              case 'conflict':
                return '⚠️';
              default:
                return '❓';
            }
          };

          const getAvailabilityColor = () => {
            if (isSelected && hasConflicts) {
              return isConflictAcknowledged
                ? 'border-amber-400 bg-amber-100 ring-2 ring-amber-200'
                : 'border-red-400 bg-red-100 ring-2 ring-red-200';
            }
            if (isSelected) return 'border-blue-400 bg-blue-100 ring-2 ring-blue-200';

            switch (coach.availability_status) {
              case 'fully_available':
                return 'border-green-300 bg-green-50 hover:border-green-400';
              case 'partly_available':
                return 'border-yellow-300 bg-yellow-50 hover:border-yellow-400';
              case 'coaching_other_crews':
                return 'border-blue-300 bg-blue-50 hover:border-blue-400';
              case 'not_available':
                return coachDeclaredUnavailable
                  ? 'border-rose-300 bg-rose-50 hover:border-rose-400'
                  : 'border-red-300 bg-red-50 hover:border-red-400';
              case 'conflict':
                return 'border-red-400 bg-red-100 hover:border-red-500';
              default:
                return 'border-gray-200 hover:border-gray-300';
            }
          };

          const getAvailabilityDescription = () => {
            switch (coach.availability_status) {
              case 'fully_available':
                return 'Fully available for this time slot';
              case 'partly_available':
                return 'Partially available - some time overlap';
              case 'coaching_other_crews':
                return 'Already coaching other crews at this time';
              case 'not_available':
                return coachDeclaredUnavailable
                  ? 'Marked unavailable for this time'
                  : 'Not available during this time slot';
              case 'conflict':
                return 'Has conflicting session scheduled';
              default:
                return 'Availability status unknown';
            }
          };

          return (
            <div key={coach.id} className="space-y-2">
              <label
                className={`flex cursor-pointer items-start space-x-3 rounded-lg border p-4 transition-all ${getAvailabilityColor()}`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onCoachToggle(coach.id)}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <div className="font-medium text-gray-900">
                      {coach.first_name && coach.last_name
                        ? `${coach.first_name} ${coach.last_name}`
                        : coach.name || 'Unknown Coach'}
                    </div>
                    <span className="text-lg">{getAvailabilityIcon()}</span>
                  </div>
                  <div className="mb-1 text-sm text-gray-600">{getAvailabilityDescription()}</div>
                  {coach.available_minutes && (
                    <div className="text-xs text-gray-500">
                      Available: {coach.available_minutes} minutes
                    </div>
                  )}

                  {/* Show conflict details for coaches with conflicting sessions */}
                  {coach.availability_status === 'conflict' &&
                    coach.availability_details?.conflicts && (
                      <div className="mt-2 rounded border border-red-100 bg-red-50 p-2 text-xs">
                        <div className="mb-1 font-medium text-red-700">Conflicting Sessions:</div>
                        {coach.availability_details.conflicts.map((conflict, index) => {
                          // Format time without seconds
                          const formatTime = (timeString) => {
                            if (!timeString) return '';
                            return timeString.substring(0, 5); // Remove seconds (HH:MM:SS -> HH:MM)
                          };

                          return (
                            <div key={index} className="mb-1 text-red-700">
                              <div className="font-medium">{conflict.session_name}</div>
                              <div className="text-red-600">
                                {conflict.crew_name && (
                                  <div className="mb-1">
                                    <span className="font-medium">Crew:</span> {conflict.crew_name}
                                  </div>
                                )}
                                <div>
                                  <span className="font-medium">Time:</span>{' '}
                                  {formatTime(conflict.start_time)}-{formatTime(conflict.end_time)}
                                  {conflict.location && ` • ${conflict.location}`}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                  {/* Legacy conflicts display for backward compatibility */}
                  {coach.conflicts &&
                    coach.conflicts.length > 0 &&
                    coach.availability_status !== 'conflict' && (
                      <div className="mt-2 rounded bg-gray-50 p-2 text-xs">
                        <div className="mb-1 font-medium text-gray-700">Conflicts:</div>
                        {coach.conflicts.map((conflict, index) => (
                          <div key={index} className="text-gray-600">
                            • {conflict.session_name} ({conflict.time_range})
                            {conflict.crew_names && ` - ${conflict.crew_names.join(', ')}`}
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              </label>

              {/* Conflict Acknowledgment */}
              {isSelected && hasConflicts && (
                <div className="ml-4 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <label className="flex cursor-pointer items-start space-x-2">
                    <input
                      type="checkbox"
                      checked={isConflictAcknowledged}
                      onChange={() => handleConflictAcknowledge(coach.id)}
                      className="mt-0.5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                    />
                    <div className="text-sm">
                      <div className="font-medium text-amber-800">
                        Acknowledge scheduling conflict
                      </div>
                      <div className="mt-1 text-xs text-amber-700">
                        I understand this coach has scheduling conflicts and choose to proceed
                        anyway.
                      </div>
                    </div>
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredCoaches.length === 0 && (
        <div className="py-8 text-center">
          <div className="text-gray-500">
            {showAdvancedView ? 'No coaches found' : 'No available coaches for this time slot'}
          </div>
          {!showAdvancedView && conflictedCoaches.length > 0 && (
            <button
              onClick={() => setShowAdvancedView(true)}
              className="mt-2 text-sm text-blue-600 underline hover:text-blue-700"
            >
              Show {conflictedCoaches.length} coach(es) with conflicts
            </button>
          )}
        </div>
      )}

      {/* Selection Summary */}
      {selectedCoaches.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <h4 className="mb-2 text-sm font-medium text-blue-900">Coach Selection Summary</h4>
          <div className="space-y-1 text-sm text-blue-800">
            <div>• {selectedCoaches.length} coach(es) selected</div>
            {validation.hasConflicts && (
              <div className="text-amber-700">
                •{' '}
                {
                  selectedCoaches.filter((id) => {
                    const coach = coaches.find((c) => c.id === id);
                    return (
                      coach &&
                      (coach.availability_status === 'coaching_other_crews' ||
                        coach.availability_status === 'not_available' ||
                        coach.availability_status === 'conflict')
                    );
                  }).length
                }{' '}
                coach(es) with scheduling conflicts
              </div>
            )}
            <div className="mt-2 text-xs text-blue-600">
              {validation.canProceed
                ? '✓ Ready to proceed to final review'
                : '⚠️ Please acknowledge all conflicts to continue'}
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!validation.canProceed}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          title={
            !validation.canProceed ? 'Please acknowledge all scheduling conflicts to continue' : ''
          }
        >
          Next: Review & Schedule
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

CoachSelectionStep.propTypes = {
  coaches: PropTypes.array.isRequired,
  selectedCoaches: PropTypes.array.isRequired,
  onCoachToggle: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default CoachSelectionStep;
